import type React from 'react';
import { useEffect } from 'react';
import { useLocation, useNavigate, Route, Routes, Navigate, Outlet } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from "../../app/hooks"
import { ProgressBar } from '@digital/react-aviva-atom';

import { Header } from '../../components/header/Header';
import { Footer } from '../../components/footer/Footer';
import ShowProducts from '../../containers/show-products/ShowProducts';
import { StickyBanner } from '../../components/sticky-banner/StickyBanner';
import { ContextualHelpDrivingSchool } from '../../components/contextual-help/ContextualHelpDrivingSchool';
import PreviousStepCompleteRoute from '../../components/previous-step-complete-route/PreviousStepCompleteRoute';

import type { RootState } from '../../app/store';
import { getProducts } from '../../redux/products/products';
import { getPickupLocations } from '../../redux/pickup/pickup';
import { getCounties } from '../../redux/pupil/counties';
import { BookingResponseType } from '../../redux/booking/booking';
import { appRoutes, appRoutesByName } from './Routes';
import Conditions from '../../components/conditions/Conditions';

import './App.css';

const App: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  
  const location = useLocation();

  const progressBarStep = useAppSelector((state: RootState) => state.progress.step);
  const product = useAppSelector((state: RootState) => {
    const productId = state.userSelection.productId;
    return productId ? state.products.availableProducts[productId] : undefined;
  });
  const confirmationCode = useAppSelector((state: RootState) => {
    const bookingResponse = state.booking.bookingResponse;
    const paymentResponse = state.payment.paymentResponse;
    const confirmationCode = bookingResponse?.code;
    const authCode = confirmationCode || (paymentResponse && atob(paymentResponse.responseObj.AUTHCODE));
    return confirmationCode || authCode;
  });
  const bookingResponseType = useAppSelector((state: RootState) => state.booking.bookingResponseType);

  useEffect(() => {
    Promise.all([
      dispatch(getProducts()),
      dispatch(getPickupLocations()),
      dispatch(getCounties())
    ]).catch(() => {
      navigate(appRoutesByName.errorPage.path);
    });
  }, [dispatch]);

  // Render sticky banner
  const renderStickyBanner = () => {
    const { details, payment, confirmation, pickup } = appRoutesByName;

    switch (location.pathname) {
      case pickup.path:
      case details.path:
      case payment.path:
        if (product) {
          return (
            <StickyBanner>
              <strong>{product.description}</strong> €{product.price}
            </StickyBanner>
          );
        }
        break;
      case confirmation.path:
        if (product) {
          return (
            <StickyBanner>
              {product.description}
              {bookingResponseType === BookingResponseType.DetailsError
                ? ' Authorisation Code: '
                : ' Confirmation Number: '}
              {confirmationCode}
            </StickyBanner>
          );
        }
        break;
      default:
        return undefined;
    }
    return undefined;
  };

  const isErrorPage = location.pathname === appRoutesByName.errorPage.path;

  return (
    <>
      <div className="flex-wrapper">
        <div className="main-container" data-testid="app-container">
          <Header />
          {renderStickyBanner()}
          <div className="app-container l-content-container">
            {!isErrorPage && (
              <ProgressBar title="Driving School" step={progressBarStep} steps={4} />
            )}
            <div>
              <Outlet />
            </div>
          </div>
          <div>
            <Conditions path={location.pathname} />
            <ContextualHelpDrivingSchool />
          </div>
        </div>
        <Footer />
      </div>
    </>
  );
};

export default App;